import * as React from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnalyticsHeaderSlot } from '@/features/Analytics/components/AnalyticsHeaderSlot/useAnalyticsHeaderSlot'
import { UsageAndCostActions } from '@/features/Analytics/components/UsageAndCostActions'
import { useAnalyticsFiltersStore } from '@/features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import useAnalyticsStore from '../../../features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { Drawer, DrawerWrapper } from '../../common'
import { UsageAndCostDataSets } from '../UsageAndCostDataSets/UsageAndCostDataSets'
import { UsageAndCostReport } from './UsageAndCostReport/UsageAndCostReport'
import { useAnalyticsFiltersGetParams } from '@/features/Analytics/hooks/useAnalyticsFiltersGetParams'
import { ResetReportSettingsButton } from '../../molecules/ResetReportSettingsButton/ResetReportSettingsButton'
import { useElementWidth } from '@/hooks'

export const UsageAndCost: React.FC = () => {
    const { t } = useTranslation('translations')
    const { setSlot } = useAnalyticsHeaderSlot()
    const toggleButtonRef = React.useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)

    React.useEffect(() => {
        if (toggleButtonRef.current) {
            toggleButtonRef.current.setAttribute(
                'data-walkthrough',
                'report-settings-toggle'
            )
        }
    }, [])

    const activeFilters = useAnalyticsFiltersStore(
        (state) => state.activeFilters
    )
    const allParams = useAnalyticsFiltersGetParams(activeFilters)

    const dataSets = useAnalyticsStore((state) => state.usageAndCost.dataSets)
    const resetActiveDataSets = useAnalyticsStore(
        (state) => state.usageAndCost.resetActiveDataSets
    )
    const hasDefaultDataSets = useAnalyticsStore(
        (state) => state.usageAndCost.hasDefaultDataSets
    )
    const isOpen = useAnalyticsStore(
        (state) => state.usageAndCost.isReportSettingsOpen
    )
    const setIsOpen = useAnalyticsStore(
        (state) => state.usageAndCost.setIsReportSettingsOpen
    )

    const handleSetIsOpen = React.useCallback(
        (value: React.SetStateAction<boolean>) => {
            if (typeof value === 'function') {
                setIsOpen(value(isOpen))
            } else {
                setIsOpen(value)
            }
        },
        [isOpen, setIsOpen]
    )

    const activeDataSets = useAnalyticsStore(
        (state) => state.usageAndCost.dataSets
    )
    const setActiveDataSets = useAnalyticsStore(
        (state) => state.usageAndCost.setActiveDataSets
    )

    const calculationMethodOffset = isOpen ? 16 : toggleWidth

    useEffect(() => {
        setSlot(<UsageAndCostActions filters={allParams} />)
        return () => {
            setSlot(null)
        }
    }, [allParams, setSlot])

    return (
        <DrawerWrapper>
            <Drawer
                headerContent={
                    <ResetReportSettingsButton
                        onClick={resetActiveDataSets}
                        isHidden={hasDefaultDataSets}
                    />
                }
                drawerToggleLabel={t('analytics.common.report_settings_title')}
                drawerToggleIconName="tune"
                title={t('analytics.common.report_settings_title')}
                titleDataWalkthrough="report-settings-drawer"
                isOpen={isOpen}
                setIsOpen={handleSetIsOpen}
                toggleButtonRef={toggleButtonRef}
            >
                <UsageAndCostDataSets
                    filters={allParams}
                    activeDataSets={activeDataSets}
                    setActiveDataSets={
                        setActiveDataSets as (dataSets: string[]) => void
                    }
                />
            </Drawer>
            <UsageAndCostReport
                filters={allParams}
                dataSets={dataSets}
                isOpen={isOpen}
                calculationMethodOffset={calculationMethodOffset}
            />
        </DrawerWrapper>
    )
}
